import React, { useState, useRef, useEffect, useMemo } from "react";
import { Search, X, User, UserPlus, Loader2, Phone as PhoneIcon } from "lucide-react";
import { Input } from "./input";
import { Button } from "./button";
import { Label } from "./label";
import { toast } from "sonner";
import * as api from "../../lib/api";
import { useApp } from "../../context/AppContext";

function normalizeString(str) {
    return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
}

function isPlaceholderEmail(email) {
    return !email || email.includes("@no-email.local");
}

export function UserSearchSelect({
    users = [],
    departments = [],
    value,
    onChange,
    placeholder = "Rechercher un utilisateur...",
    filterByDepartments,
    excludeUserIds,
    disabled = false,
    showDepartment = true,
    allowCreateExternal = false,
    "data-testid": testId = "user-search-select",
}) {
    const { refreshUsers, currentUser } = useApp();
    const [query, setQuery] = useState("");
    const [open, setOpen] = useState(false);
    const containerRef = useRef(null);

    const isITStaff = currentUser?.role === "admin" || currentUser?.role === "agent";
    const inputRef = useRef(null);

    // External contact creation state
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [creating, setCreating] = useState(false);
    const [extForm, setExtForm] = useState({ first_name: "", last_name: "", phone_fixed: "", phone_mobile: "" });

    const getDeptName = (deptId) => {
        if (!deptId || !showDepartment) return "";
        const d = departments.find((dep) => dep.id === deptId);
        return d ? d.name : "";
    };

    const candidates = useMemo(() => {
        let list = users;
        if (filterByDepartments?.length) {
            list = list.filter((u) => filterByDepartments.includes(u.department_id));
        }
        if (excludeUserIds?.length) {
            list = list.filter((u) => !excludeUserIds.includes(u.id));
        }
        return list;
    }, [users, filterByDepartments, excludeUserIds]);

    const selectedUser = useMemo(
        () => (value ? candidates.find((u) => u.id === value) || users.find((u) => u.id === value) : null),
        [value, candidates, users],
    );

    const results = useMemo(() => {
        if (query.length < 2) return [];
        const q = normalizeString(query);
        return candidates
            .filter((u) => {
                const haystack = normalizeString(
                    `${u.first_name} ${u.last_name} ${u.email} ${getDeptName(u.department_id)}`,
                );
                return haystack.includes(q);
            })
            .slice(0, 10);
    }, [query, candidates]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleSelect = (user) => {
        onChange(user.id);
        setQuery("");
        setOpen(false);
    };

    const handleClear = (e) => {
        e.stopPropagation();
        onChange(null);
        setQuery("");
    };

    const handleFocus = () => {
        if (!disabled) {
            setOpen(true);
            if (selectedUser) setQuery("");
        }
    };

    const handleInputChange = (e) => {
        setQuery(e.target.value);
        if (!open) setOpen(true);
    };

    const handleCreateExternal = async () => {
        if (!extForm.first_name.trim() || !extForm.last_name.trim()) {
            toast.error("Prénom et nom sont obligatoires");
            return;
        }
        setCreating(true);
        try {
            const created = await api.createUser({
                first_name: extForm.first_name.trim(),
                last_name: extForm.last_name.trim(),
                phone_fixed: extForm.phone_fixed.trim() || null,
                phone_mobile: extForm.phone_mobile.trim() || null,
                user_type: "external",
                email: "",
                role: "user",
            });
            toast.success(`Contact externe créé : ${created.first_name} ${created.last_name}`);
            await refreshUsers();
            onChange(created.id);
            setShowCreateForm(false);
            setExtForm({ first_name: "", last_name: "", phone_fixed: "", phone_mobile: "" });
            setQuery("");
            setOpen(false);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erreur lors de la création");
        } finally {
            setCreating(false);
        }
    };

    return (
        <div ref={containerRef} className="relative" data-testid={testId}>
            <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                <input
                    ref={inputRef}
                    type="text"
                    value={open || !selectedUser ? query : `${selectedUser.first_name} ${selectedUser.last_name}`}
                    onChange={handleInputChange}
                    onFocus={handleFocus}
                    placeholder={selectedUser && !open ? `${selectedUser.first_name} ${selectedUser.last_name}` : placeholder}
                    disabled={disabled}
                    className={`w-full pl-9 pr-9 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white ${
                        disabled ? "opacity-50 cursor-not-allowed bg-gray-50" : ""
                    } ${selectedUser && !open ? "text-gray-900 font-medium" : ""}`}
                    data-testid={`${testId}-input`}
                />
                {selectedUser && !disabled && (
                    <button
                        type="button"
                        onClick={handleClear}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-0.5 rounded-full hover:bg-gray-100"
                        data-testid={`${testId}-clear`}
                    >
                        <X className="w-4 h-4" />
                    </button>
                )}
            </div>

            {open && query.length >= 2 && !showCreateForm && (
                <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto" data-testid={`${testId}-dropdown`}>
                    {results.length > 0 ? (
                        results.map((u) => {
                            const deptName = getDeptName(u.department_id);
                            const displayEmail = isPlaceholderEmail(u.email) ? null : u.email;
                            return (
                                <button
                                    key={u.id}
                                    type="button"
                                    onClick={() => handleSelect(u)}
                                    className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-gray-50 border-b last:border-b-0 transition-colors"
                                    data-testid={`${testId}-option-${u.id}`}
                                >
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${u.user_type === "external" ? "bg-amber-100 text-amber-700" : "bg-gray-200 text-gray-600"}`}>
                                        {u.first_name?.[0]}{u.last_name?.[0]}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-medium flex items-center gap-1.5">
                                            {u.first_name} {u.last_name}
                                            {u.user_type === "external" && (
                                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-700">EXT</span>
                                            )}
                                        </div>
                                        <div className="text-xs text-gray-500 truncate">
                                            {displayEmail || (u.phone_mobile || u.phone_fixed || "Contact externe")}
                                            {deptName ? ` — ${deptName}` : ""}
                                        </div>
                                    </div>
                                </button>
                            );
                        })
                    ) : (
                        <div className="px-3 py-3 text-sm text-gray-400 flex items-center gap-2">
                            <User className="w-4 h-4" />
                            Aucun utilisateur trouvé
                        </div>
                    )}
                    {allowCreateExternal && isITStaff && (
                        <button
                            type="button"
                            onClick={() => {
                                setShowCreateForm(true);
                                // Pre-fill name from query
                                const parts = query.trim().split(/\s+/);
                                setExtForm({
                                    first_name: parts[0] || "",
                                    last_name: parts.slice(1).join(" ") || "",
                                    phone_fixed: "",
                                    phone_mobile: "",
                                });
                            }}
                            className="w-full flex items-center gap-2 px-3 py-2.5 text-left text-sm font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 border-t border-gray-200 transition-colors"
                            data-testid={`${testId}-create-external-btn`}
                        >
                            <UserPlus className="w-4 h-4" />
                            + Créer un contact externe
                        </button>
                    )}
                </div>
            )}

            {/* Inline External Contact Creation Form */}
            {showCreateForm && (
                <div className="absolute z-50 mt-1 w-full bg-white border border-amber-300 rounded-lg shadow-lg p-4" data-testid={`${testId}-create-external-form`}>
                    <div className="flex items-center gap-2 mb-3">
                        <UserPlus className="w-4 h-4 text-amber-600" />
                        <span className="text-sm font-semibold text-amber-800">Nouveau contact externe</span>
                    </div>
                    <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                <Label className="text-xs text-gray-600">Prénom *</Label>
                                <Input
                                    value={extForm.first_name}
                                    onChange={(e) => setExtForm({ ...extForm, first_name: e.target.value })}
                                    placeholder="Prénom"
                                    className="h-8 text-sm"
                                    data-testid={`${testId}-ext-firstname`}
                                />
                            </div>
                            <div>
                                <Label className="text-xs text-gray-600">Nom *</Label>
                                <Input
                                    value={extForm.last_name}
                                    onChange={(e) => setExtForm({ ...extForm, last_name: e.target.value })}
                                    placeholder="Nom"
                                    className="h-8 text-sm"
                                    data-testid={`${testId}-ext-lastname`}
                                />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                <Label className="text-xs text-gray-600">Tél. fixe</Label>
                                <Input
                                    value={extForm.phone_fixed}
                                    onChange={(e) => setExtForm({ ...extForm, phone_fixed: e.target.value })}
                                    placeholder="+41 ..."
                                    className="h-8 text-sm"
                                    data-testid={`${testId}-ext-phone-fixed`}
                                />
                            </div>
                            <div>
                                <Label className="text-xs text-gray-600">Tél. mobile</Label>
                                <Input
                                    value={extForm.phone_mobile}
                                    onChange={(e) => setExtForm({ ...extForm, phone_mobile: e.target.value })}
                                    placeholder="+41 ..."
                                    className="h-8 text-sm"
                                    data-testid={`${testId}-ext-phone-mobile`}
                                />
                            </div>
                        </div>
                        <div className="flex items-center gap-2 pt-1">
                            <Button
                                size="sm"
                                onClick={handleCreateExternal}
                                disabled={creating || !extForm.first_name.trim() || !extForm.last_name.trim()}
                                className="bg-amber-600 hover:bg-amber-700 text-white text-xs h-8"
                                data-testid={`${testId}-ext-submit`}
                            >
                                {creating ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <UserPlus className="w-3 h-3 mr-1" />}
                                Créer
                            </Button>
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setShowCreateForm(false)}
                                className="text-xs h-8"
                                data-testid={`${testId}-ext-cancel`}
                            >
                                Annuler
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
